import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  Page,
  PageSections,
  updatePage,
  SECTION_LABELS,
  SECTION_FIELD_LABELS,
} from '@/lib/pages-service';
import { Loader2, Save, FileText, Globe, AlertCircle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

interface PageEditorDialogProps {
  page: Page | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPageSaved: () => void;
}

const PageEditorDialog = ({
  page,
  open,
  onOpenChange,
  onPageSaved,
}: PageEditorDialogProps) => {
  const { toast } = useToast();
  const { role } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Permission checks
  const isEditor = role === 'editor';
  const canEditSEO = role === 'superadmin' || role === 'admin';
  
  const [formData, setFormData] = useState({
    name: '',
    meta_title: '',
    meta_description: '',
    sections: {} as PageSections,
  });

  // Reset form when page changes
  useEffect(() => {
    if (page) {
      setFormData({
        name: page.name,
        meta_title: page.meta_title || '',
        meta_description: page.meta_description || '',
        sections: page.sections || {},
      });
    }
  }, [page]);

  const handleSectionFieldChange = (
    sectionKey: string,
    fieldKey: string,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionKey]: {
          ...prev.sections[sectionKey],
          [fieldKey]: value,
        },
      },
    }));
  };

  const handleSubmit = async () => {
    if (!page) return;

    // Validation
    if (formData.meta_description && formData.meta_description.length > 170) {
      toast({
        title: 'Warning',
        description: 'Meta description is longer than recommended (170 characters)',
        variant: 'destructive',
      });
    }

    setLoading(true);
    try {
      const updates: Partial<Page> = {
        sections: formData.sections,
      };

      // Only include SEO fields if user has permission
      if (canEditSEO) {
        updates.meta_title = formData.meta_title || undefined;
        updates.meta_description = formData.meta_description || undefined;
        updates.name = formData.name;
      }

      await updatePage(page.id, updates);
      
      toast({
        title: 'Page Updated',
        description: `"${page.name}" has been saved successfully`,
      });

      onOpenChange(false);
      onPageSaved();
    } catch (error: any) {
      console.error('Error saving page:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save page',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getFieldLabel = (key: string): string => {
    return SECTION_FIELD_LABELS[key] || key.charAt(0).toUpperCase() + key.slice(1);
  };

  const getSectionLabel = (key: string): string => {
    return SECTION_LABELS[key] || key.charAt(0).toUpperCase() + key.slice(1);
  };

  const renderSectionFields = (sectionKey: string, section: Record<string, any>) => {
    return Object.entries(section).map(([fieldKey, value]) => {
      if (typeof value !== 'string') return null;

      const isLongText = fieldKey === 'body' || fieldKey === 'bio' || value.length > 100;
      
      return (
        <div key={fieldKey} className="space-y-2">
          <Label htmlFor={`${sectionKey}-${fieldKey}`} className="text-sm font-medium">
            {getFieldLabel(fieldKey)}
          </Label>
          {isLongText ? (
            <Textarea
              id={`${sectionKey}-${fieldKey}`}
              value={value}
              onChange={(e) => handleSectionFieldChange(sectionKey, fieldKey, e.target.value)}
              rows={4}
              className="resize-none"
            />
          ) : (
            <Input
              id={`${sectionKey}-${fieldKey}`}
              value={value}
              onChange={(e) => handleSectionFieldChange(sectionKey, fieldKey, e.target.value)}
            />
          )}
        </div>
      );
    });
  };

  if (!page) return null;

  const sectionKeys = Object.keys(formData.sections);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Edit Page: {page.name}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            <Badge variant="outline">
              <Globe className="h-3 w-3 mr-1" />
              {page.language.toUpperCase()}
            </Badge>
            <span className="text-muted-foreground">Slug: {page.slug}</span>
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="content" className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="seo" disabled={isEditor}>
              SEO {isEditor && '(Admin Only)'}
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1 mt-4">
            <TabsContent value="content" className="mt-0 space-y-4">
              {sectionKeys.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                  <p>No editable sections found for this page.</p>
                </div>
              ) : (
                <Accordion type="multiple" defaultValue={sectionKeys} className="w-full">
                  {sectionKeys.map((sectionKey) => (
                    <AccordionItem key={sectionKey} value={sectionKey}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="font-mono text-xs">
                            {sectionKey}
                          </Badge>
                          <span className="font-medium">{getSectionLabel(sectionKey)}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 pt-2 pl-2">
                          {renderSectionFields(sectionKey, formData.sections[sectionKey])}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </TabsContent>

            <TabsContent value="seo" className="mt-0 space-y-6">
              {/* Page Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Page Name (Internal)</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Page name for admin reference"
                />
              </div>

              <Separator />

              {/* Meta Title */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="meta_title">Meta Title</Label>
                  <span className={`text-xs ${formData.meta_title.length > 60 ? 'text-destructive' : 'text-muted-foreground'}`}>
                    {formData.meta_title.length}/60 characters
                  </span>
                </div>
                <Input
                  id="meta_title"
                  value={formData.meta_title}
                  onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                  placeholder="SEO title for search engines"
                />
                <p className="text-xs text-muted-foreground">
                  Recommended: 50-60 characters. Include main keyword.
                </p>
              </div>

              {/* Meta Description */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="meta_description">Meta Description</Label>
                  <span className={`text-xs ${formData.meta_description.length > 160 ? 'text-destructive' : 'text-muted-foreground'}`}>
                    {formData.meta_description.length}/160 characters
                  </span>
                </div>
                <Textarea
                  id="meta_description"
                  value={formData.meta_description}
                  onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                  placeholder="Brief description for search results"
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  Recommended: 150-160 characters. Include target keywords naturally.
                </p>
              </div>

              {/* SEO Preview */}
              <div className="space-y-2">
                <Label>Search Result Preview</Label>
                <div className="border rounded-lg p-4 bg-muted/50">
                  <p className="text-blue-600 text-lg font-medium truncate">
                    {formData.meta_title || 'Page Title'}
                  </p>
                  <p className="text-green-700 text-sm">
                    https://danielalbertlaw.com{page.slug}
                  </p>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {formData.meta_description || 'Page description will appear here...'}
                  </p>
                </div>
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PageEditorDialog;